/**
 * uploadClient - authenticated client side of the Evidencer upload protocol
 *
 *   1. login()          POST /api/login -> signed session token (kept in localStorage)
 *   2. uploadToBlob()   direct-to-Blob upload (multipart for big files) using a
 *                       token from POST /api/get-upload-url
 *   3. pinToIpfs()      POST /api/pin-ipfs - server streams the blob to Filebase
 *                       and re-verifies the SHA-256
 *
 * The browser never holds Airtable, Filebase or Blob credentials.
 */

const SESSION_KEY = 'evidencer_session';
const MULTIPART_THRESHOLD = 8 * 1024 * 1024;

export function saveSession({ token, expiresAt }) {
  localStorage.setItem(SESSION_KEY, JSON.stringify({ token, expiresAt }));
}

export function clearSession() {
  localStorage.removeItem(SESSION_KEY);
}

export function getSessionToken() {
  try {
    const session = JSON.parse(localStorage.getItem(SESSION_KEY) || 'null');
    if (session?.token && session.expiresAt > Date.now()) return session.token;
  } catch {
    /* fall through */
  }
  return null;
}

function requireToken() {
  const token = getSessionToken();
  if (!token) throw new Error('Your session has expired. Please log in again.');
  return token;
}

export async function login(username, password) {
  const response = await fetch('/api/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data.success) {
    return { success: false, error: data.error || `Login failed (${response.status})` };
  }
  saveSession(data);
  return { success: true, user: data.user };
}

export function safeFileName(name) {
  const cleaned = String(name || 'file').replace(/[^A-Za-z0-9._-]/g, '_').replace(/^\.+/, '');
  return (cleaned || 'file').slice(0, 120);
}

/**
 * Upload straight to Vercel Blob. `sha256` must be the hex SHA-256 of the exact
 * bytes being uploaded (the ciphertext, for encrypted evidence).
 */
export async function uploadToBlob(file, sha256, { onProgress } = {}) {
  const authToken = requireToken();
  const { upload } = await import('@vercel/blob/client');

  const blob = await upload(`evidence/${sha256}/${safeFileName(file.name)}`, file, {
    access: 'public',
    handleUploadUrl: '/api/get-upload-url',
    clientPayload: JSON.stringify({ authToken, sha256 }),
    contentType: file.type || 'application/octet-stream',
    multipart: file.size > MULTIPART_THRESHOLD,
    onUploadProgress: onProgress ? ({ percentage }) => onProgress(percentage) : undefined,
  });

  return blob; // { url, pathname, contentType, ... }
}

export async function pinToIpfs({ blobUrl, sha256, fileName, description, caseNumber }) {
  const response = await fetch('/api/pin-ipfs', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${requireToken()}` },
    body: JSON.stringify({ blobUrl, sha256, fileName, description, caseNumber }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data.success) {
    throw new Error(data.error || `IPFS pinning failed (${response.status})`);
  }
  return data;
}
